/* eslint-disable complexity */
import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SingleValue } from 'react-select';
import { ReportSubstantiation, UpdateReport } from 'src/api';
import { Button } from 'src/components/atoms';
import { BodyText } from 'src/components/atomsLegacy';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { DefaultOption, handleSuccessToast } from 'src/components/molecules';
import { SelectDropdownGeneric } from 'src/components/moleculesLegacy';
import { getReadableReportSource } from 'src/components/pages/Case/helpers/getReadableReportSource';
import {
  setDeterminedCategory,
  setMiReportSource,
  setReportedCategory,
} from 'src/creators';
import { AppDispatch } from 'src/store';
import {
  CategoryDisplayType,
  MiReportSource,
  useGetDeterminedCategoriesQuery,
  useGetManualIntakeReportedCategoriesQuery,
  useUpdateDeterminedCategoryMutation,
  useUpdateMiReportSourceMutation,
  useUpdateReportedCategoryMutation,
} from 'src/graphql';
import { useWindowDimensions } from 'src/hooks';
import theme, { ThemeType } from 'src/theme/theme';
import { MiReportSourceForDisplay } from 'src/types';
import { SubstantiationSelector } from '../../moleculesLegacy/SubstantiationSelector';
import { DraftReportCaseAttributes } from '../DraftReportCaseAttributes';
import { ReporterDetails } from '../ReporterDetails';
import { Container } from '../ReporterDetails/ReporterDetails.styles';
import {
  BottomContainer,
  DetailContainer,
  DetailsContainer,
  ReportDetailsContainer,
  ReportDetailsToggleStyled,
  Text,
  Title,
  TitleContainer,
  TitleText,
} from './DraftReportDetails.styles';

type Manager = { name: string };

interface Props {
  reportID: string;
  currentManager: Manager;
  currentReport: UpdateReport | null;
  cannotManageCases: boolean;
  updateReport: (id: string, report: UpdateReport) => void;
  setIsAddingReporter: (isAddingReporter: boolean) => void;
  isExistingReport?: boolean;
  isUpdateRelationshipToOrganisationEnabled?: boolean;
}

export const DraftReportDetails: FC<Props & { theme: ThemeType }> = ({
  isExistingReport = false,
  ...props
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { width } = useWindowDimensions();
  const pastBreakpoint = width < 680;
  const [isCollapsed, setIsCollapsed] = useState(true);

  const { data: reportedCategories } =
    useGetManualIntakeReportedCategoriesQuery();
  const { data: determinedCategories } = useGetDeterminedCategoriesQuery();

  const { mutate: updateReportedCategory } =
    useUpdateReportedCategoryMutation();

  const { mutate: updateDeterminedCategory } =
    useUpdateDeterminedCategoryMutation();

  const { mutate: updateMiReportSource } = useUpdateMiReportSourceMutation();

  const reportedTo = props.cannotManageCases
    ? 'Assigned randomly after submission'
    : `${props.currentManager.name} (you)`;

  const miReportSourceList = Object.values(MiReportSource) as MiReportSource[];
  const readableReportSource = getReadableReportSource(
    props.currentReport?.miReportSource,
  );
  const miReportSourceLabel =
    readableReportSource &&
    readableReportSource !== MiReportSourceForDisplay.UNSPECIFIED
      ? readableReportSource
      : '';

  return (
    <ReportDetailsContainer isVisible={!isCollapsed || !pastBreakpoint}>
      <ReportDetailsToggle
        onClick={() => setIsCollapsed(!isCollapsed)}
        isCollapsed={isCollapsed}
        pastBreakpoint={pastBreakpoint}
      />
      <TitleContainer isVisible={!isCollapsed || !pastBreakpoint}>
        <TitleText weight="regular" noMargin={true}>
          Report Details
        </TitleText>
      </TitleContainer>
      <DetailsContainer isVisible={!isCollapsed || !pastBreakpoint}>
        <DetailContainer key={'reported-to'} first>
          <Title>Reported To</Title>
          <BodyText
            weight="regular"
            margin="4px 0 0"
            data-testid="assignee-name"
          >
            {reportedTo}
          </BodyText>
        </DetailContainer>
        {isExistingReport && (
          <DraftReportCaseAttributes reportID={props.reportID} />
        )}
        <DetailContainer key={'source'}>
          <Title>Source</Title>
          <SelectDropdownGeneric
            options={miReportSourceList.map((value) => ({
              value,
              label: getReadableReportSource(value),
            }))}
            value={{
              label: miReportSourceLabel,
              value: props.currentReport?.miReportSource ?? '',
            }}
            label={''}
            onChange={
              ((e: { value: MiReportSource }) =>
                updateMiReportSource(
                  {
                    miReportSource: e.value,
                    report: props.reportID,
                  },
                  {
                    onSuccess: () => {
                      handleSuccessToast({
                        toastMessage: 'Report has been successfully updated',
                      });
                      dispatch(setMiReportSource(e.value as MiReportSource));
                    },
                  },
                )) as (value: SingleValue<DefaultOption>) => void
            }
            customStyle={{
              controlStyle: {
                backgroundColor: theme.newPalette.neutral.N200,
                height: '28px',
                margin: '4px 0 0 0',
              },
            }}
            menuPlacement="bottom"
            menuPosition="fixed"
          />
        </DetailContainer>
        <DetailContainer key={'reported-category'}>
          <Title>Reported Category (Required)</Title>
          <SelectDropdownGeneric
            options={
              reportedCategories?.getManualIntakeReportedCategories?.map(
                (value) => ({
                  value: value.name,
                  label: value.name,
                  id: value.id,
                }),
              ) ?? []
            }
            value={{
              label: props.currentReport?.reportedCategory?.name ?? '',
              value: props.currentReport?.reportedCategory?.name ?? '',
            }}
            label={''}
            onChange={
              ((e: { id: number; value: string }) =>
                updateReportedCategory(
                  {
                    reportedCategoryId: e.id,
                    report: props.reportID,
                  },
                  {
                    onSuccess: () => {
                      handleSuccessToast({
                        toastMessage: 'Report has been successfully updated',
                      });
                      dispatch(
                        setReportedCategory({
                          id: e.id,
                          name: e.value,
                        } as CategoryDisplayType),
                      );
                    },
                  },
                )) as unknown as (value: SingleValue<DefaultOption>) => void
            }
            customStyle={{
              controlStyle: {
                backgroundColor: theme.newPalette.neutral.N200,
                height: '28px',
                margin: '4px 0 0 0',
              },
            }}
            menuPlacement="bottom"
            menuPosition="fixed"
            className="report-sidebar-submission-type-field"
            classNamePrefix="submission-type"
          />
        </DetailContainer>
        <DetailContainer key={'determined-category'}>
          <Title>Determined Category</Title>
          <SelectDropdownGeneric
            options={
              determinedCategories?.getDeterminedCategories?.map((value) => ({
                value: value.name,
                label: value.name,
                id: value.id,
              })) ?? []
            }
            value={{
              label: props.currentReport?.determinedCategory?.name ?? '',
              value: props.currentReport?.determinedCategory?.name ?? '',
            }}
            label={''}
            onChange={
              ((e: { id: number; value: string }) =>
                updateDeterminedCategory(
                  {
                    determinedCategoryId: e.id,
                    report: props.reportID,
                  },
                  {
                    onSuccess: () => {
                      handleSuccessToast({
                        toastMessage: 'Report has been successfully updated',
                      });
                      dispatch(
                        setDeterminedCategory({
                          id: e.id,
                          name: e.value,
                        } as CategoryDisplayType),
                      );
                    },
                  },
                )) as unknown as (value: SingleValue<DefaultOption>) => void
            }
            customStyle={{
              controlStyle: {
                backgroundColor: theme.newPalette.neutral.N200,
                height: '28px',
                margin: '4px 0 0 0',
              },
            }}
            menuPlacement="bottom"
            menuPosition="fixed"
            className="report-sidebar-issue-type-field"
            classNamePrefix="issue-type"
          />
        </DetailContainer>
        <DetailContainer>
          <Title>Substantiation Status</Title>
          <SubstantiationSelector
            currentReport={props.currentReport}
            reportSubstantiationList={
              Object.values(ReportSubstantiation) as ReportSubstantiation[]
            }
            updateReport={props.updateReport}
          />
        </DetailContainer>
      </DetailsContainer>
      <BottomContainer isVisible={!isCollapsed || !pastBreakpoint}>
        {props.currentReport?.User?.email ? (
          <Container>
            <ReporterDetails
              isDraftReport
              reportID={props.reportID}
              currentReport={props.currentReport}
              reporter={props.currentReport.User}
              updateReport={props.updateReport}
              setIsAddingReporter={props.setIsAddingReporter}
              reporterRelationshipType={
                props.currentReport.relationshipToCompany ??
                props.currentReport.User.relationshipToCompany
              }
              isUpdateRelationshipToOrganisationEnabled={
                props.isUpdateRelationshipToOrganisationEnabled
              }
            />
          </Container>
        ) : (
          <>
            <Text>
              If reporter&apos;s identity is disclosed, either select from the
              employee directory or manually enter details; if reporter is
              anonymous, leave blank.
            </Text>
            <Button
              onClick={() => props.setIsAddingReporter(true)}
              data-testid="add-reporter"
              variant="primary"
              size="small"
              style={{ alignSelf: 'flex-start', marginLeft: '24px' }}
            >
              <StyledIconInButton
                size="small"
                position="left"
                className="uil uil-plus"
              />
              Add reporter details
            </Button>
          </>
        )}
      </BottomContainer>
    </ReportDetailsContainer>
  );
};

interface ReportDetailsToggleProps {
  onClick: () => void;
  isCollapsed: boolean;
  pastBreakpoint: boolean;
}

const ReportDetailsToggle: React.FC<ReportDetailsToggleProps> = ({
  onClick,
  isCollapsed,
  pastBreakpoint,
}) => {
  return (
    <>
      {pastBreakpoint && (
        <ReportDetailsToggleStyled onClick={onClick}>
          {isCollapsed ? (
            <img
              src="/assets/open-nav-arrow-right.png"
              alt="Open report details"
            />
          ) : (
            <img
              src="/assets/open-nav-arrow-left.png"
              alt="Close report details"
            />
          )}
        </ReportDetailsToggleStyled>
      )}
    </>
  );
};
